import { Injectable, computed, signal } from '@angular/core';
import { StorageService } from '@core/services/storage.service';
import { TASK_CARD_DEADLINE_COLUMN_WIDTH } from '@shared/modules/list/consts/task-card-columns.const';

export type MyWorkColumn = 'project' | 'priority' | 'minutes' | 'deadline' | 'comment';

const COLUMN_WIDTHS: Record<MyWorkColumn, string> = {
  project: '220px',
  priority: '120px',
  minutes: '90px',
  deadline: TASK_CARD_DEADLINE_COLUMN_WIDTH,
  comment: 'minmax(0, 1fr)'
};

const DEFAULT_COLUMNS: Record<MyWorkColumn, boolean> = {
  project: true,
  priority: true,
  minutes: true,
  deadline: true,
  comment: true
};

@Injectable({ providedIn: 'root' })
export class MyWorkColumnsService {
  readonly columns: MyWorkColumn[] = ['project', 'priority', 'minutes', 'deadline', 'comment'];
  private readonly visibility = signal<Record<MyWorkColumn, boolean>>({ ...DEFAULT_COLUMNS });
  private storageKey: string | null = null;

  readonly gridTemplateColumns = computed(() => {
    const tracks = this.columns
      .filter((column) => this.visibility()[column])
      .map((column) => COLUMN_WIDTHS[column]);
    return tracks.length ? tracks.join(' ') : 'minmax(0, 1fr)';
  });

  constructor(private readonly storage: StorageService) {}

  initialize(userId: number | null | undefined): void {
    this.storageKey = userId ? `myWorkCardColumns:${userId}` : null;
    let saved: Partial<Record<MyWorkColumn, boolean>> = {};
    if (this.storageKey) {
      try {
        const value = JSON.parse(this.storage.get(this.storageKey) || '{}');
        if (value && typeof value === 'object' && !Array.isArray(value)) saved = value;
      } catch {
        // Invalid browser state falls back to the default layout.
      }
    }
    this.visibility.set(
      this.columns.reduce((result, column) => {
        result[column] = typeof saved[column] === 'boolean' ? saved[column] : true;
        return result;
      }, {} as Record<MyWorkColumn, boolean>)
    );
  }

  isVisible(column: MyWorkColumn): boolean {
    return this.visibility()[column];
  }

  toggle(column: MyWorkColumn): void {
    this.visibility.update((current) => ({ ...current, [column]: !current[column] }));
    if (this.storageKey) {
      this.storage.put(this.storageKey, JSON.stringify(this.visibility()));
    }
  }
}
